import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import PortalLayout from "@/portal/PortalLayout";
import MyProfile from "@/portal/MyProfile";
import MyProperties from "@/portal/MyProperties";
import KanbanBoard from "@/portal/KanbanBoard";
import UserManagement from "@/portal/UserManagement";
import Allocations from "@/portal/Allocations";
import ClientProfiles from "@/portal/ClientProfiles";
import ProductSetup from "@/portal/ProductSetup";
import Products from "@/portal/Products";
import Orders from "@/portal/Orders";
import Invoices from "@/portal/Invoices";
import ContractorInvoice from "@/portal/ContractorInvoice";
import ContractorInvoices from "@/portal/ContractorInvoices";
import MyInvoices from "@/portal/MyInvoices";
import PaymentResult from "@/pages/PaymentResult";
import PayInvoice from "@/pages/PayInvoice";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading || user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="h-10 w-10 rounded-full border-2 border-emerald-200 border-t-emerald-600 animate-spin" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading || user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="h-10 w-10 rounded-full border-2 border-emerald-200 border-t-emerald-600 animate-spin" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/portal/properties" replace />;
  return children;
};

const StaffRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading || user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="h-10 w-10 rounded-full border-2 border-emerald-200 border-t-emerald-600 animate-spin" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (!["admin", "power_user", "legal_rep", "accountant"].includes(user.role)) return <Navigate to="/portal/properties" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" theme="light" richColors />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/payment/success" element={<PaymentResult />} />
            <Route path="/payment/cancel" element={<PaymentResult cancelled />} />
            <Route path="/pay-invoice/:id" element={<PayInvoice />} />
            <Route path="/portal" element={<ProtectedRoute><PortalLayout /></ProtectedRoute>}>
              <Route index element={<MyProperties />} />
              <Route path="profile" element={<MyProfile />} />
              <Route path="properties" element={<MyProperties />} />
              <Route path="sale" element={<KanbanBoard board="sale" />} />
              <Route path="purchase" element={<KanbanBoard board="purchase" />} />
              <Route path="tenancy" element={<KanbanBoard board="tenancy" />} />
              <Route path="maintenance" element={<KanbanBoard board="maintenance" />} />
              <Route path="users" element={<AdminRoute><UserManagement /></AdminRoute>} />
              <Route path="allocations" element={<AdminRoute><Allocations /></AdminRoute>} />
              <Route path="client-profiles" element={<StaffRoute><ClientProfiles /></StaffRoute>} />
              <Route path="product-setup" element={<AdminRoute><ProductSetup /></AdminRoute>} />
              <Route path="invoices" element={<AdminRoute><Invoices /></AdminRoute>} />
              <Route path="contractor-invoices" element={<AdminRoute><ContractorInvoices /></AdminRoute>} />
              <Route path="contractor-invoice" element={<ContractorInvoice />} />
              <Route path="products" element={<Products />} />
              <Route path="orders" element={<Orders />} />
              <Route path="my-invoices" element={<MyInvoices />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
